import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  Typography
} from '@mui/material';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';

import { response } from '../../../api/db';
import {
  errorColor,
  gray100,
  primaryButtonBackgroundColor
} from '../../../constants/Colors';
import { headerHeight, spHeaderHeight } from '../../../constants/Location';
import { common } from '../../../modules/error';
import { memberRegisterInfoSchema } from '../../../validation/user';
import type { MemberRegisterInfoType } from '../../../validation/user/userType';
import { registerMemberInfo } from '../../../api/db/member';
import { userLogin } from '../../../api/db/user';

import CustomHalfTextfield from '../../uiParts/CustomHalfTextfield';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import Header from '../../uiParts/Header';
import PasswordTextfield from '../../uiParts/PasswordTextfield';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  button: {
    fontSize: '16px',
    fontWeight: '500',
    height: '44px',
    mt: '16px'
  },
  container: {
    m: `${headerHeight} auto 264px`,
    pt: '56px',
    width: '560px',
    gap: '24px',
    '@media screen and (max-width:600px)': {
      m: `${spHeaderHeight} auto 204px`,
      pt: '24px',
      width: '90%'
    }
  },
  halfTextfieldWrapper: {
    '@media screen and (max-width:600px)': {
      width: '49%'
    }
  },
  label: {
    color: gray100,
    fontSize: '14px',
    mb: '12px'
  },
  labelRequired: {
    color: errorColor,
    fontSize: '14px',
    marginLeft: '8px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '32px',
      fontWeight: '500'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px'
  }
};

const MemberRegisterInfo: React.FC = () => {
  const location = useLocation();
  const userId = location.state?.id;
  const email = location.state?.email;
  const token = location.state?.token;

  const navigate = useNavigate();

  const [errorText, setErrorText] = useState('');
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    trigger
  } = useForm<MemberRegisterInfoType>({
    defaultValues: {
      firstName: '',
      firstNameKana: '',
      lastName: '',
      lastNameKana: '',
      password: '',
      passwordConfirm: '',
      terms: false
    },
    resolver: yupResolver(memberRegisterInfoSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('firstName') === undefined ||
        '' ||
        watch('firstNameKana') === undefined ||
        '' ||
        watch('lastName') === undefined ||
        '' ||
        watch('lastNameKana') === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        watch('passwordConfirm') === undefined ||
        '' ||
        watch('terms') === false ||
        Boolean(errors.firstName) ||
        Boolean(errors.firstNameKana) ||
        Boolean(errors.lastName) ||
        Boolean(errors.lastNameKana) ||
        Boolean(errors.password) ||
        Boolean(errors.passwordConfirm) ||
        Boolean(errors.terms)
      );
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await registerMemberInfo(userId, email, token, watch);
      await userLogin(email, watch('password'));
      navigate('/auth/member/register/complete');
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      if (error instanceof Error) {
        sentryLog(error);
        if (axiosError.isAxiosError && axiosError.response) {
          const loginErrorResponse = axiosError.response as response;
          setErrorText(loginErrorResponse.data.message);
          setOpen(true);
        } else {
          setErrorText(common);
          setOpen(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const nextJsBaseUrl = process.env.REACT_APP_NEXT_JS_BASE_URL;

  return (
    <>
      <CustomHead title="共有者登録 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Header />
      <Box sx={styles.container}>
        <Typography sx={styles.title}>共有者登録</Typography>
        <form>
          <Box sx={{ mb: '24px' }}>
            <Typography sx={styles.label}>
              共有者
              <Typography component="span" sx={styles.labelRequired}>
                必須
              </Typography>
            </Typography>
            <Grid container justifyContent="space-between">
              <Grid item sx={styles.halfTextfieldWrapper}>
                <Controller
                  render={({ field }) => (
                    <CustomHalfTextfield
                      {...field}
                      error={Boolean(errors.lastName)}
                      helperText={errors.lastName?.message}
                      labelText="姓"
                      placeholder="山田"
                    />
                  )}
                  control={control}
                  name="lastName"
                />
              </Grid>
              <Grid item sx={styles.halfTextfieldWrapper}>
                <Controller
                  render={({ field }) => (
                    <CustomHalfTextfield
                      {...field}
                      error={Boolean(errors.firstName)}
                      helperText={errors.firstName?.message}
                      labelText="名"
                      placeholder="太郎"
                    />
                  )}
                  control={control}
                  name="firstName"
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ mb: '24px' }}>
            <Typography sx={styles.label}>
              共有者(フリガナ)
              <Typography component="span" sx={styles.labelRequired}>
                必須
              </Typography>
            </Typography>
            <Grid container justifyContent="space-between">
              <Grid item sx={styles.halfTextfieldWrapper}>
                <Controller
                  render={({ field }) => (
                    <CustomHalfTextfield
                      {...field}
                      error={Boolean(errors.lastNameKana)}
                      helperText={errors.lastNameKana?.message}
                      labelText="セイ"
                      placeholder="ヤマダ"
                    />
                  )}
                  control={control}
                  name="lastNameKana"
                />
              </Grid>
              <Grid item sx={styles.halfTextfieldWrapper}>
                <Controller
                  render={({ field }) => (
                    <CustomHalfTextfield
                      {...field}
                      error={Boolean(errors.firstNameKana)}
                      helperText={errors.firstNameKana?.message}
                      labelText="メイ"
                      placeholder="タロウ"
                    />
                  )}
                  control={control}
                  name="firstNameKana"
                />
              </Grid>
            </Grid>
          </Box>
          <Controller
            render={({ field }) => (
              <PasswordTextfield
                {...field}
                error={Boolean(errors.password)}
                formMb="24px"
                helperText={errors.password?.message}
                labelText="パスワード"
                requiredText="必須"
                onChange={async (e) => {
                  field.onChange(e);
                  if (firstSubmit) {
                    await trigger('passwordConfirm');
                  }
                }}
              />
            )}
            control={control}
            name="password"
          />
          <Controller
            render={({ field }) => (
              <PasswordTextfield
                {...field}
                error={Boolean(errors.passwordConfirm)}
                formMb="24px"
                helperText={errors.passwordConfirm?.message}
                labelText="パスワード(確認用)"
                requiredText="必須"
              />
            )}
            control={control}
            name="passwordConfirm"
          />
          <Controller
            render={({ field }) => (
              <FormControl error={Boolean(errors.terms)}>
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label={
                    <Typography style={{ fontSize: '12px' }}>
                      <Link
                        href={nextJsBaseUrl + '/term'}
                        target="_blank"
                        style={{ color: '#000', textDecoration: 'underline' }}
                      >
                        利用規約
                      </Link>
                      、
                      <Link
                        href={'https://www.tokyu-cnst.co.jp/privacy'}
                        target="_blank"
                        style={{ color: '#000', textDecoration: 'underline' }}
                      >
                        プライバシーポリシー
                      </Link>
                      に同意します。
                    </Typography>
                  }
                />
                {errors.terms && (
                  <FormHelperText>{errors.terms.message}</FormHelperText>
                )}
              </FormControl>
            )}
            name="terms"
            control={control}
            defaultValue={false}
          />
          <Button
            disabled={buttonDisabledFlag()}
            fullWidth
            onClick={() => {
              void (async () => {
                setFirstSubmit(true);
                await handleSubmit(async () => await onSubmit())();
              })();
            }}
            sx={[primaryButtonBackgroundColor, styles.button]}
            variant="contained"
          >
            登録する
          </Button>
        </form>
        <CustomSnackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          text={errorText}
        />
      </Box>
    </>
  );
};

export default MemberRegisterInfo;
